import React, { useState, useRef } from "react";
import styled from "@emotion/styled";
import { TextField, Button, CircularProgress } from "@mui/material";
import { ContactMaster, SortingOptions, ContactDescription } from "../types";
import { getImageUrl } from "../utils";
import Image from "./image";
import { theme } from "../theme";
import debounce from "debounce";
import { ReactComponent as XCircle } from "../assets/x-circle.svg";
import { ReactComponent as NetworkIcon } from "../assets/network.svg";
import { connect } from "react-redux";
import { RootState } from "../redux/root-store";
import dayjs from "dayjs";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    align-items: stretch;
    @media only screen and (max-width: 720px) {
    }
`;

const Wrapper = styled(Container)`
    //border-right: 1px solid #ccc;
    @media only screen and (max-width: 720px) {
        width: 100%;
        position: absolute;
        left: 0;
        top: 0;
        padding-top: 1em;
        border-right: 0;
    }
`;

const Row = styled.div`
    width: 100%;
    display: flex;
`;

const SplitRow = styled(Row)`
    & > * {
        flex: 1;
        flex-grow: 1;
    }
`;

const ContactRow = styled(Row)`
    align-items: center;
    position: relative;
    display: flex;
    flex-direction: column;
    width: 80%;
    margin: 0 auto;
    &:hover {
        background-color: ${theme.palette.grey2.light};
        cursor: pointer;
    }
`;

const ContactTop = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 70px;
`;

const ContactImage = styled(Image)`
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    margin-right: 1rem;
`;
const ContactName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    font-size: 1.2rem;
    flex: 1;
    & > b {
        color: ${theme.palette.primary.main};
    }
`;
const FactMatch = styled("span")(
    ({ theme, $isLast }: any) => `
    color: ${theme.palette.grey2.main};
    margin-left: 7rem;
    margin-right: 3.5rem;
    width: calc(100% - 3.5em);
    ${(!$isLast && `border-bottom: 1px solid #ccc;`) || ""}
    & > b {
        color: ${theme.palette.primary.main};
    }
`
);

const mapDispatchToProps = {};
const mapStateToProps = (state: RootState) => ({
    contacts: state.contacts.list,
    allFacts: state.contacts.allFacts,
});
type dispatchType = typeof mapDispatchToProps;

interface Props extends dispatchType, ReturnType<typeof mapStateToProps> {
    isLoadingContacts: boolean;
    containerProps?: React.CSSProperties;
    onContactClick?: (contact: ContactMaster) => void;
    onAddNewContact?: () => void;
    sortingOptions: SortingOptions;
    setSortingOptions: (
        data: SortingOptions | ((sorting: SortingOptions) => SortingOptions)
    ) => void;
}

const Search = (props: Props) => {
    const { sortingOptions, contacts, allFacts } = props;

    const contactSearchDates = React.useRef<{ [contact_id: string]: string }>(
        {}
    );

    const debouncedSetSortingOptions = React.useCallback(
        debounce(props.setSortingOptions, 500),
        [props.setSortingOptions]
    );

    const searchRef = useRef<HTMLInputElement>(null);

    const getDisplayName = (contact: ContactMaster): string => {
        const start = contact.name
            .toLowerCase()
            .indexOf(sortingOptions.keyword.toLowerCase());
        if (start === -1) return contact.name;
        return `${contact.name.substr(0, start)}<b>${contact.name.substr(
            start,
            sortingOptions.keyword.length
        )}</b>${contact.name.substr(start + sortingOptions.keyword.length)}`;
    };
    const handleContactSort = (sortingOptions: SortingOptions) => {
        if (
            sortingOptions.recent === 1 ||
            sortingOptions.recent === 2 ||
            sortingOptions.keyword.length > 0
        ) {
            return (a: ContactMaster, b: ContactMaster) => {
                const aDate = contactSearchDates.current[a.contact_id];
                const bDate = contactSearchDates.current[b.contact_id];
                if (!aDate && !bDate) return 0;
                if (!aDate) return 1;
                if (!bDate) return -1;
                if (aDate === bDate) return 0;
                if (aDate < bDate) return 1;
                return -1;
            };
            /*
            return (a: ContactMaster, b: ContactMaster) => {
                if (!a.updated_date && !b.updated_date) return 0;
                if (!a.updated_date) return 1;
                if (!b.updated_date) return -1;
                if (a.updated_date === b.updated_date) return 0;
                if (a.updated_date < b.updated_date) return 1;
                return -1;
            };
             */
        } else if (sortingOptions.alphabetical === 1) {
            return (a: ContactMaster, b: ContactMaster) => {
                if (a.name === b.name) return 0;
                if (a.name > b.name) return 1;
                return -1;
            };
        } else if (sortingOptions.alphabetical === 2) {
            return (a: ContactMaster, b: ContactMaster) => {
                const aName = a.name.split(" ")[1] || "";
                const bName = b.name.split(" ")[1] || "";
                if (aName === bName) return 0;
                if (aName > bName) return 1;
                return -1;
            };
        }
    };

    const handleContactFilter = (sortingOptions: SortingOptions) => {
        if (sortingOptions.keyword.length > 0) {
            return (a: ContactMaster) => {
                return (
                    (a.searchMatches && a.searchMatches.length > 0) ||
                    sortingOptions.contactIds[a.contact_id] ||
                    false
                );
            };
        }
        if (sortingOptions.tagName) {
            return (a: ContactMaster) => {
                return sortingOptions.contactIds[a.contact_id] || "";
            };
        }
        if (sortingOptions.howYouMet || sortingOptions.howYouMetId) {
            return (a: ContactMaster) => {
                return (
                    a.howyoumeet === sortingOptions.howYouMet &&
                    a.howyoumeet_id === sortingOptions.howYouMetId
                );
            };
        }
        if (sortingOptions.recent === 1) {
            const currentDate = dayjs().endOf("day");
            return (a: ContactMaster) => {
                if (dayjs(a.updated_date).isAfter(currentDate)) return false;
                return true;
            };
        } else {
            return (a: ContactMaster) => {
                return true;
            };
        }
    };

    const lowercaseSortingKeyword = sortingOptions.keyword.toLowerCase();
    const markSearchMatch = (
        lowercase: string,
        normal: string,
        keyword: string
    ): string => {
        let matchIndex = lowercase.indexOf(keyword);
        const matches: number[] = [];
        if (matchIndex > -1) {
            matches.push(matchIndex);
            matchIndex = lowercase.indexOf(keyword, matchIndex + 1);
        }
        for (let match = matches.length - 1; match >= 0; match--) {
            const start = matches[match];
            normal =
                normal.substr(0, start) +
                "<b>" +
                normal.substr(start, keyword.length) +
                "</b>" +
                normal.substr(start + keyword.length);
        }
        return normal;
    };
    const matchFact = (fact: ContactDescription, keyword: string): string[] => {
        const matches: string[] = [];
        if (fact.lowercaseDescription?.includes(keyword)) {
            matches.push(
                markSearchMatch(
                    fact.lowercaseDescription,
                    fact.description,
                    keyword
                )
            );
        } else if (fact.lowercaseKidName?.includes(keyword)) {
            matches.push(
                markSearchMatch(fact.lowercaseKidName, fact.kidName, keyword)
            );
        } else if (fact.lowercaseLocation?.includes(keyword)) {
            matches.push(
                markSearchMatch(fact.lowercaseLocation, fact.location, keyword)
            );
        }

        return matches;
    };
    const matchingFacts = React.useMemo(() => {
        contactSearchDates.current = contacts.reduce(
            (acc: { [contact_id: string]: string }, val: ContactMaster) => {
                return { ...acc, [val.contact_id]: val.updated_date || "" };
            },
            {}
        );
        return !lowercaseSortingKeyword.trim()
            ? {}
            : allFacts
                  .map((fact: ContactDescription) => {
                      const matches = matchFact(fact, lowercaseSortingKeyword);
                      if (
                          matches.length &&
                          fact.updated_date &&
                          fact.updated_date >
                              contactSearchDates.current[fact.contact_id]
                      ) {
                          contactSearchDates.current[fact.contact_id] =
                              fact.updated_date;
                      }
                      return {
                          contact_id: fact.contact_id,
                          matches,
                      };
                  })
                  .reduce(
                      (
                          acc: { [contact_id: string]: string[] },
                          val: { contact_id: string; matches: string[] }
                      ) => ({
                          ...acc,
                          [val.contact_id]: [
                              ...(acc[val.contact_id] || []),
                              ...val.matches,
                          ],
                      }),
                      {}
                  );
    }, [allFacts, lowercaseSortingKeyword]);

    const searchResultContacts = React.useMemo(() => {
        return contacts
            .map((contact: ContactMaster) => ({
                ...contact,
                searchMatches: [
                    ...(matchingFacts[contact.contact_id] || []),
                    ...(lowercaseSortingKeyword &&
                    contact.name.toLowerCase().includes(lowercaseSortingKeyword)
                        ? [""]
                        : []),
                    ...(sortingOptions.contactIds[contact.contact_id]
                        ? [`Group <b>${sortingOptions.tagName}</b>`]
                        : []),
                ],
            }))
            .filter(handleContactFilter(sortingOptions))
            .sort(handleContactSort(sortingOptions));
    }, [contacts, sortingOptions]);

    return (
        <Wrapper
            style={{
                height: "100%",
                ...props.containerProps,
            }}
        >
            <Container style={{ width: "80%", margin: "0 auto" }}>
                <TextField
                    inputRef={searchRef}
                    placeholder={
                        sortingOptions.howYouMet ||
                        sortingOptions.tagName ||
                        sortingOptions.keyword ||
                        "Search"
                    }
                    defaultValue={props.sortingOptions.keyword}
                    onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                        debouncedSetSortingOptions(
                            (sorting: SortingOptions) => ({
                                ...sorting,
                                keyword: evt.target.value,
                                contactIds: {},
                                tagName: "",
                            })
                        );
                    }}
                />
                <XCircle
                    style={{
                        position: "absolute",
                        top: "1.1rem",
                        right: "1rem",
                        cursor: "pointer",
                    }}
                    onClick={() => {
                        if (searchRef.current) {
                            searchRef.current.value = "";
                        }
                        debouncedSetSortingOptions(
                            (sorting: SortingOptions) => ({
                                ...sorting,
                                keyword: "",
                                tagName: "",
                                contactIds: {},
                                howYouMet: "",
                                howYouMetId: "",
                            })
                        );
                    }}
                />
                <Row style={{ marginTop: "0.5em" }}>
                    <Button
                        style={{ flexGrow: 1 }}
                        onClick={() => {
                            props.setSortingOptions({
                                recent: (props.sortingOptions.recent % 2) + 1,
                                alphabetical: 0,
                                keyword: props.sortingOptions.keyword,
                                tagName: "",
                                contactIds: {},
                            });
                        }}
                    >
                        Most Recent
                    </Button>
                    <Button
                        style={{ flexGrow: 1 }}
                        onClick={() => {
                            props.setSortingOptions({
                                alphabetical:
                                    (props.sortingOptions.alphabetical % 2) + 1,
                                recent: 0,
                                tagName: "",
                                contactIds: {},
                                keyword: props.sortingOptions.keyword,
                            });
                        }}
                    >
                        Alphabetical
                    </Button>
                </Row>
            </Container>
            <Container
                style={{
                    flexGrow: 1,
                    overflow: "auto",
                    paddingBottom: "2em",
                    alignItems: "flex-start",
                }}
            >
                {props.isLoadingContacts && (
                    <Row
                        style={{ justifyContent: "center", margin: "50px 0px" }}
                    >
                        <CircularProgress />
                    </Row>
                )}
                {searchResultContacts.map(
                    (contact: ContactMaster) =>
                        contact.name && (
                            <ContactRow
                                key={contact.contact_id}
                                onClick={() =>
                                    props.onContactClick &&
                                    props.onContactClick(contact)
                                }
                            >
                                <ContactTop>
                                    <ContactImage
                                        src={getImageUrl(
                                            contact.person_profile
                                        )}
                                    />
                                    <ContactName
                                        dangerouslySetInnerHTML={{
                                            __html: getDisplayName(contact),
                                        }}
                                    ></ContactName>
                                    {contact.original_person_id && (
                                        <NetworkIcon />
                                    )}
                                </ContactTop>
                                {contact.searchMatches &&
                                    contact.searchMatches.map(
                                        (match: string, index: number) => (
                                            <FactMatch
                                                $isLast={
                                                    contact.searchMatches &&
                                                    index ===
                                                        contact.searchMatches
                                                            .length -
                                                            1
                                                }
                                                key={index}
                                                dangerouslySetInnerHTML={{
                                                    __html: match,
                                                }}
                                            />
                                        )
                                    )}
                                <div
                                    style={{
                                        width: "100%",
                                        position: "absolute",
                                        left: "0px",
                                        bottom: "0px",
                                        margin: "0 auto",
                                        borderBottom: "1px solid black",
                                    }}
                                />
                            </ContactRow>
                        )
                )}
            </Container>
            {props.onAddNewContact && (
                <div
                    style={{
                        position: "absolute",
                        bottom: "1rem",
                        right: "1rem",
                        display: "flex",
                        justifyContent: "end",
                        paddingRight: "1em",
                    }}
                >
                    <Button
                        style={{
                            position: "fixed",
                            bottom: "1rem",
                            borderRadius: "50%",
                            fontWeight: "500",
                            minWidth: "1.5em",
                            minHeight: "1.5em",
                            width: "1.5em",
                            height: "1.5em",
                            fontSize: "2em",
                            alignItems: "unset",
                            lineHeight: "1.1em",
                        }}
                        onClick={props.onAddNewContact}
                        variant="contained"
                    >
                        +
                    </Button>
                </div>
            )}
        </Wrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Search);
